import {AutoComplete, Errors, RichText, TextInput, WithForm, WithImageInput} from '@startlibs/form';
import {Button, Icon, Loading, SplitColumnsContainer} from '@startlibs/components'
import {callIfFunction, ifUndefined} from '@startlibs/utils';
import {useNavigate, useLocation, useParams} from '@startlibs/components'
import {useRefState, useToggle} from '@startlibs/core'
import React, {useRef} from 'react'
import {FormattedMessage, useIntl} from 'react-intl'
import _ from 'lodash/fp'
import {BoxPageContainer, BoxPageWrapper, BoxWrapperFooter, BoxWrapperHeading} from '../../components/BoxPageLayout';
import {ImageInputContainer, ProfilePage, ThumbnailContainer} from './components/ExpertProfileLayout';
import {PurviewFooter} from '../../components/PurviewFooter';
import {
  buildValidation,
  intlConfirmEmails, intlEmailValidation,
  intlRequired
} from '../../utils/validation';
import {getJwt} from '../../hooks/useJwt'
import {jwtGetFetcher, jwtPostFetcher} from '../../utils/authFetch'
import {useResetExpertPasswordDialog} from './hooks/useResetExpertPasswordDialog'
import {willUseSuspense} from '../../hooks/useSuspense'
import {lazyExperts} from '../steps/finalReport/reportPages/lazyExperts'
import {ConfirmEmailInput} from '../../components/ConfirmEmailInput'
import {IfLocale, useIsLocale} from '../../hocs/IfLocale'
import { lazyProviderInfo } from '../../components/WithProvider';
import { isRichText, RTF_TOOLBAR_OPTIONS_STARTLIBS, safeMultipleNewLineToBr, safeMultipleNewLineToBrOnly } from '../../utils/utils';
import { FeedbackDialog } from '../../expert/dialogs/FeedbackDialog';

// Specialties autocomplete:
let fetchedSuggestions = null

const fetchSuggestions = (s) => {
  const fetch = fetchedSuggestions || jwtGetFetcher(getJwt())("/api/specializations").then(_.map(({label}) => label))
  fetchedSuggestions = fetch
  return fetch.then(specializations =>
    specializations.filter(t => t.toLowerCase().indexOf(s) >= 0)
  ).then(l => l.indexOf(s) >= 0 ? l : l.concat([{newValue: s}]))
}

const getLabel = (intl) => (v) =>
  _.isString(v.newValue)
    ? intl.formatMessage({
      defaultMessage: `Create option "{newOption}"`,
      description: "EditExpert create new specialization"
    },{newOption:v.newValue})
    : v
const transform = (l) => l ? l.map(v => (v && v.newValue) || v) : l

/************************/

// Image upload
const uploadFetcher = (expertId) => (file) => {
  const formData = new FormData()
  formData.append('file', file)
  return jwtPostFetcher(getJwt())(`/api/expert/${expertId}/picture`, formData).then(console.log)
}

/*****************/


const useExpertViewSuspense = willUseSuspense((id) =>
  (!id && !_.isString(id))
    ? Promise.resolve()
    // : jwtGetFetcher(getJwt())(`/api/expert/${id}`)
    : id ? jwtGetFetcher(getJwt())(`/api/expert/${id}`) : jwtGetFetcher(getJwt())(`/api/expert`)
    .then(_.update('info.specialization', (v) => v || []))
)

const EMPTY_EXPERT = {info: {specialization: []}}
// const updateResumeToRTF = (expert) => _.update('info.resume', (resume) => (resume && resume.includes("\n")) ? safeMultipleNewLineToBr(resume)  : resume, expert)
// const updateResumeToRTF = (expert) => _.update('info.resume', (resume) => resume && isRichText(resume) && resume.includes("\n") ? safeMultipleNewLineToBrOnly(resume) : isRichText(resume) ? resume : safeMultipleNewLineToBr(resume),expert)
const updateResumeToRTF = (expert) => _.update('info.resume', (resume) => resume && resume.includes("\n") ? isRichText(resume) ? safeMultipleNewLineToBrOnly(resume) : safeMultipleNewLineToBr(resume) : resume,expert)
const addConfirmEmail = (expert) => _.update("picture",(image) => !!image, expert ? updateResumeToRTF({...expert, confirmEmail: expert.email}) : updateResumeToRTF(expert))
const addEmptySpecialization = (expert) => _.update("info.specialization", (v) => v || [], addConfirmEmail(expert))
export const EditExpert = ({action, onSuccess, footer, isIncomplete, emptyId, feedbackPopup = false}) => {
  const intl = useIntl()
  const {id} = useParams()
  const expertId = id || emptyId;
  const location = useLocation()
  const navigate = useNavigate()
  const onSuccessWithDefault = onSuccess || (() => navigate(location.state?.nextLocation || ("/admin/experts/view/"+id)))
  const providerInfo = lazyProviderInfo.read()
  const expert = useExpertViewSuspense(expertId)
  const formRef = useRef()
  const openResetPassword = useResetExpertPasswordDialog(expert)
  const fileToUpload = useRefState()
  const uploadingFile = useToggle()
  const errorRef = useRef()
  
  const checkImageAndScroll = (file) => {
    if (file.size > 4194304/4) {
      setTimeout(() => errorRef.current.scrollIntoView({block: 'center', behavior: 'smooth'}), 50)
      return [intl.formatMessage({
        defaultMessage:'Your file needs to be smaller than 1mb.',
        description:"EditExpert image upload size error"
      })]
    }
    if (['image/gif', 'image/png', 'image/jpeg', 'image/jpg'].indexOf(file.type.toLowerCase()) < 0) {
      setTimeout(() => errorRef.current.scrollIntoView({block: 'center', behavior: 'smooth'}), 50)
      return [intl.formatMessage({
        defaultMessage: 'Invalid file format. Please select a .JPG or .PNG file.',
        description:"EditExpert image upload format error"
      })]
    }
  }

  const isNotLegal = useIsLocale({not:true,contains:"LEGAL"})

  const preValidation = buildValidation({
      firstName: intlRequired(intl),
      lastName: intlRequired(intl),
      email: [intlRequired(intl), intlEmailValidation(intl), intlConfirmEmails(intl)('confirmEmail')],
      confirmEmail: intlRequired(intl),
      'info.specialization': [v => {return (!v || !v.filter(Boolean).length > 0) && isNotLegal && intlRequired(intl)()}],
    }
  )


  const setFile = (file) => {
    fileToUpload.set(file)
    const image = URL.createObjectURL(file)
    formRef.current.setValue("picture",image)
    return Promise.resolve({image})
  }

  const onSubmit = (values) => {
    if (fileToUpload.get()) {
      uploadingFile.open()
    }
    return action(values)
      .then((expert) => {
        const uploadId = expert.id || id
        if (fileToUpload.get()) {
          return uploadFetcher(uploadId)(fileToUpload.get()).then(() => expert)
        } else {
          return expert
        }
      })
      .finally(() => uploadingFile.close())
  }

  const pictureId = ifUndefined(expert && expert.id,id)


  const onFailure = (e,[,{status}],form) => status === 409 && form.setError("email",
    intl.formatMessage({
      defaultMessage:"There's an expert already registered in the system with that email.",
      description:"EditExpert email already exists error"
    })
  )

  return <>
    <WithForm
      alwaysSave
      onFailure={onFailure}
      onSuccess={(values,resp) => {
        lazyExperts.refresh()
        onSuccessWithDefault(values,resp)
      }} ref={formRef} preValidation={preValidation} action={onSubmit}
                    // transform={_.update("info.specialization",_.filter(_.get("length")))}
                    transform={_.update(
                      "info.specialization",
                      _.filter(v => Boolean(v && v.trim()))
                    )}
                    values={addEmptySpecialization(expert) || EMPTY_EXPERT}>{form =>
      <ProfilePage>
        <BoxPageContainer>
        <BoxPageWrapper>

          <BoxWrapperHeading>
            {
              isIncomplete
                ? <h2><FormattedMessage
                  defaultMessage="Please complete your profile"
                  description="EditExpert title for incomplete profile"
                /></h2>
                : expert
                ? <h2><FormattedMessage
                    defaultMessage="Expert: <span>{hasName, select, true {{name}} other {{email}}}</span>"
                    values={{
                      hasName: !!(expert.firstName || expert.lastName),
                      name: `${expert.firstName} ${expert.lastName}`,
                      email: expert.email,
                      span: (chunks) => <span>{chunks}</span>
                    }}
                    description="EditExpert title for existing expert"
                  /></h2>
                : <h2><FormattedMessage defaultMessage='Add a new expert' description='EditExpert title for new'/></h2>
            }
          </BoxWrapperHeading>

          <div className="profile-content">

            <div className="fields-wrapper">
              <SplitColumnsContainer viewportMinWidth={850}>
                <TextInput
                  readOnly={form.isLoading}
                  autoFocus
                  label={intl.formatMessage({
                    defaultMessage:"First name:",
                    description:"EditExpert first name input label"
                  })}
                  path="firstName"
                  placeholder={intl.formatMessage({
                    defaultMessage: "John",
                    description: "EditExpert first name input placeholder"
                  })}
                  mandatory
                />
                <TextInput
                  readOnly={form.isLoading}
                  label={intl.formatMessage({
                    defaultMessage: "Last name:",
                    description: "EditExpert last name input label"
                  })}
                  path="lastName"
                  placeholder={intl.formatMessage({
                    defaultMessage: "Doe",
                    description: "EditExpert last name input placeholder"
                  })}
                  mandatory
                />
              </SplitColumnsContainer>
              <SplitColumnsContainer viewportMinWidth={850}>
                <TextInput
                  readOnly={form.isLoading}
                  label={intl.formatMessage({
                    defaultMessage: "Email:",
                    description: "EditExpert email input label"
                  })}
                  path="email"
                  placeholder={intl.formatMessage({
                    defaultMessage: "expert@email.com",
                    description: "EditExpert email input placeholder"
                  })}
                  mandatory
                />
                <ConfirmEmailInput
                  readOnly={form.isLoading}
                  label={intl.formatMessage({
                    defaultMessage: "Confirm email:",
                    description: "EditExpert confirm email input label"
                  })}
                  path="confirmEmail"
                  confirmingPath='email'
                  placeholder={intl.formatMessage({
                    defaultMessage: "expert@email.com",
                    description: "EditExpert confirm email input placeholder"
                  })}
                  mandatory
                />
              </SplitColumnsContainer>
              <SplitColumnsContainer
                viewportMinWidth={850}
                className="split-wrapper"
              >
                <TextInput
                  readOnly={form.isLoading}
                  label={intl.formatMessage({
                      defaultMessage: "Phone:",
                      description: "EditExpert phone input label"
                  })}
                  constraint={/[^\d-,+ ]/}
                  path="info.phone"
                  placeholder={intl.formatMessage({
                    defaultMessage: "+1 123-456-789",
                    description: "EditExpert phone input placeholder"
                  })}
                />
                <IfLocale not contains="LEGAL"><AutoComplete
                  mandatory
                  readOnly={form.isLoading}
                  label={intl.formatMessage({
                    defaultMessage: "Specialization(s):",
                    description: "EditExpert specialization input label"
                  })}
                  confirmIfValueKeys={['Tab']}
                  path="info.specialization"
                  fetchSuggestions={fetchSuggestions}
                  getLabel={getLabel(intl)}
                  transform={transform}
                  bellowDescText={intl.formatMessage({
                    defaultMessage: "Use comma, enter or tab to add multiple.",
                    description: "EditExpert specialization input description"
                  })}
                  className="specializations-input"
                /></IfLocale>
              </SplitColumnsContainer>
              {/* <TextInput
                readOnly={form.isLoading}
                label={<FormattedMessage defaultMessage="Resume:" description="expert resume label"/>}
                path="info.resume"
                textarea
                autoResize
              /> */}
              <RichText
                path="info.resume"
                label={<FormattedMessage defaultMessage="Resume:" description="expert resume label"/>}
                formatTools={RTF_TOOLBAR_OPTIONS_STARTLIBS}
                autoHideToolbar
                disabled={form.isLoading}
              />
            </div>

            <div className="image-wrapper">
              <WithImageInput preValidation={checkImageAndScroll} uploadFetcher={setFile}
                              path={["picture"]}>{(input, {image, isLoading}) =>
                <ImageInputContainer hasImage={image} image={image===true ? `/api/expert/${pictureId}/picture?t=${getJwt()}&no-cache=${expert.picture}` : image}>
                  {!(uploadingFile.isOpen || form.isLoading || isLoading) && input}
                  <ThumbnailContainer css={image === true ? `background-image:url('/api/expert/${pictureId}/picture?t=${getJwt()}&no-cache=${expert.picture}');` : (image && `background-image:url('${image}')`)}>
                    {!image && <Icon icon="physician"/>}
                    {(uploadingFile.isOpen || isLoading) && <Loading white absolute/>}
                  </ThumbnailContainer>
                  {!image ?
                    <Button small disabled={uploadingFile.isOpen || form.isLoading || isLoading}><FormattedMessage
                      defaultMessage="Add picture"
                      description="EditExpert add picture button"
                    /></Button>
                    : <Button small highlight disabled={uploadingFile.isOpen || form.isLoading || isLoading}>
                      <FormattedMessage defaultMessage="Change image" description="EditExpert change image button"/>
                    </Button>
                  }
                </ImageInputContainer>
              }</WithImageInput>
            </div>

          </div>
          <div ref={errorRef}>
            <Errors/>
          </div>

          <BoxWrapperFooter>
            {
              callIfFunction(footer,form, expert, providerInfo) || <>
                {
                  expert && ((!providerInfo.ssoEnabled) || expert.email.indexOf('@purview') >= 0) && 
                  <div className="left-block">
                    <Button onClick={openResetPassword}>Reset password</Button>
                  </div>
                }
                <div className="right-block">
                  <Button onClick={() => form.confirm(expert ? "discard-expert-edit" : "discard-expert-create")}>
                    <FormattedMessage defaultMessage="Cancel" description="Cancel button"/>
                  </Button>
                  <Button isLoading={form.isLoading} highlight type="submit">{expert
                    ? <FormattedMessage defaultMessage="Update" description="Update button"/>
                    : <FormattedMessage defaultMessage="Add expert" description="EditExpert add expert button"/>}</Button>
                </div>
              </>
            }
          </BoxWrapperFooter>
        </BoxPageWrapper>
        </BoxPageContainer>
        <PurviewFooter />
      </ProfilePage>
      
    }
    </WithForm>
    {
      feedbackPopup && feedbackPopup?.isOpen && <FeedbackDialog feedbackPopup={feedbackPopup} />
    }
  </>
}
